import React from "react";
import { motion } from "framer-motion";
import { Bot, FileText, Headphones } from "lucide-react";
import knowhowImg from "../../assets/image/home/knowhow.png";

function Card({ className = "", ...props }) {
  return <div className={`bg-white ${className}`} {...props} />;
}

function CardContent({ className = "", ...props }) {
  return <div className={className} {...props} />;
}

const features = [
  {
    icon: <Bot className="h-5 w-5" />,
    title: "AI 홍보 콘텐츠 자동 생성과 예약 업로드",
    desc: "매장 사진과 메뉴 정보만 있으면 AI가 홍보 이미지를 만들고, 등록한 일정에 맞춰 자동 업로드까지 이어집니다.",
  },
  {
    icon: <FileText className="h-5 w-5" />,
    title: "매장 운영 코칭 리포트",
    desc: "상권 분석과 업로드 반응 데이터를 바탕으로 매장 운영에 바로 쓰는 리포트를 제공합니다.",
  },
  {
    icon: <Headphones className="h-5 w-5" />,
    title: "전화 기반 AI 컨시어지 지원",
    desc: "설정, 운영, 수정 방법까지 전화 상담 기반으로 지원해 디지털 도구가 익숙하지 않아도 사용할 수 있습니다.",
  },
];

const steps = [
  {
    step: "STEP 1",
    title: "스마트상점 플랫폼 회원가입",
    desc: "대표자 명의로 스마트상점 플랫폼 회원가입을 진행합니다.",
  },
  {
    step: "STEP 2",
    title: "지원 사업 신청서 작성",
    desc: "플랫폼에서 업체 소개와 지원 동기, 사용 계획을 작성해 온라인으로 제출합니다.",
  },
  {
    step: "STEP 3",
    title: "wizMarket 기술 보급 선택",
    desc: "기술 공급 기업 목록 중 wizMarket을 선택해 신청을 완료합니다.",
  },
];

function TopHeader() {
  return (
    <header className="sticky top-0 z-30 border-b border-white/10 bg-black/85 backdrop-blur">
      <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4">
        <a href="/" className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-emerald-400 to-indigo-500 text-lg font-bold text-white">
            W
          </div>
          <div>
            <p className="text-lg font-bold text-white">wizMarket</p>
            <p className="text-xs text-white/60">AI 마케팅 컨시어지 안내</p>
          </div>
        </a>

        <div className="flex items-center gap-3">
          <a
            href="/concierge/manage/login"
            className="inline-flex h-12 items-center rounded-xl bg-white/10 px-5 text-sm font-medium text-white transition hover:bg-white/20"
          >
            컨시어지 관리
          </a>
          <a
            href="/help"
            className="inline-flex h-12 items-center rounded-xl bg-white/10 px-5 text-sm font-medium text-white transition hover:bg-white/20"
          >
            고객센터
          </a>
        </div>
      </div>
    </header>
  );
}

export default function WizMarketConcierge() {
  return (
    <div className="min-h-screen bg-white pb-28 text-gray-900">
      <TopHeader />

      <section className="bg-[#0f1117] px-6 py-20 text-white">
        <div className="mx-auto max-w-6xl text-center">
          <div className="mb-5 inline-flex rounded-full border border-indigo-400/30 bg-indigo-500/15 px-4 py-2 text-sm font-medium text-indigo-200">
            2026 스마트상점 기술보급사업 공급기업
          </div>

          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6 text-4xl font-bold leading-tight md:text-5xl"
          >
            AI 마케팅 자동화 서비스 wizMarket
            <br />
            스마트상점 기술보급 신청 안내
          </motion.h1>

          <p className="mx-auto mb-6 max-w-3xl text-lg text-white/80">
            소상공인시장진흥공단 스마트상점 기술보급사업 공급기업으로 선정된 AI 마케팅
            서비스 <b className="text-white">wizMarket</b> 지원 신청 페이지입니다.
          </p>

          <p className="mx-auto mb-10 max-w-3xl text-white/70">
            매장 사진만 있으면 SNS 홍보 콘텐츠가 자동으로 생성되고, 전화 상담 기반
            컨시어지 지원으로 누구나 쉽게 사용할 수 있습니다.
          </p>
        </div>
      </section>

      <section className="mx-auto grid max-w-6xl gap-6 px-6 py-16 md:grid-cols-3">
        {features.map((feature) => (
          <Card key={feature.title} className="rounded-2xl shadow-sm">
            <CardContent className="p-6">
              <div className="mb-4 text-indigo-600">{feature.icon}</div>
              <h3 className="mb-2 text-lg font-semibold">{feature.title}</h3>
              <p className="text-sm text-gray-600">{feature.desc}</p>
            </CardContent>
          </Card>
        ))}
      </section>

      <section id="apply-steps" className="mx-auto max-w-5xl px-6 py-20">
        <div className="mb-12 text-center">
          <h2 className="mb-4 text-3xl font-bold">스마트상점 기술보급 신청 절차</h2>
          <p className="text-gray-600">
            아래 절차에 따라 스마트상점 플랫폼에서 신청할 수 있습니다.
          </p>
        </div>

        <div className="grid gap-6 md:grid-cols-3">
          {steps.map((item) => (
            <Card key={item.step} className="rounded-2xl shadow-sm">
              <CardContent className="p-6">
                <h3 className="mb-3 text-lg font-semibold">{item.step}</h3>
                <p className="mb-2 font-medium">{item.title}</p>
                <p className="text-sm text-gray-600">{item.desc}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="mt-10 rounded-2xl border border-gray-200 bg-gray-50 p-6 text-sm text-gray-600">
          <p className="mb-2 font-semibold text-gray-900">신청 안내</p>
          <p>- 신청기간: 2026년 3월 13일 오전 10시 ~ 4월 1일 오후 5시</p>
          <p>- 신청방법: 스마트상점 플랫폼 회원가입 후 온라인 신청</p>
          <p>- 작성 항목: 업체 소개, 지원 동기, 사용 계획</p>
          <p className="mt-4">
            신청이 어렵다면 고객센터(02-2655-9614)로 연락해 주세요.
          </p>
        </div>
      </section>

      <section id="inquiry-form" className="mx-auto max-w-4xl px-6 py-20">
        <div className="mb-10 text-center">
          <h2 className="mb-3 text-3xl font-bold">
            wizMarket 선택용 스마트상점 사업 신청서 작성 예시
          </h2>
          <p className="text-gray-600">
            스마트상점 플랫폼 신청서 작성 시 참고할 수 있도록 예시 이미지를 제공합니다.
          </p>
        </div>

        <div className="rounded-2xl border border-gray-200 bg-gray-50 p-6 text-sm text-gray-700">
          <p className="mb-2 font-semibold text-gray-900">작성 포인트</p>
          <p className="mb-4">
            AI 마케팅 자동화 서비스인 wizMarket을 도입하려는 목적이 잘 드러나도록 아래
            항목 위주로 작성해 주세요.
          </p>
          <div className="space-y-2">
            <p>- 업체 소개(매장명, 업종, 위치, 현재 운영 상황)</p>
            <p>- 지원 동기</p>
            <p>- 사용 계획</p>
          </div>
        </div>

        <section className="mt-10 w-full">
          <div className="relative w-full">
            <img
              src={knowhowImg}
              alt="wizMarket 신청서 예시"
              className="block h-auto w-full"
              style={{ objectFit: "contain" }}
            />

            <a
              href="https://buly.kr/DPVbPT9"
              target="_blank"
              rel="noreferrer"
              aria-label="신청 페이지로 이동"
              className="absolute left-1/2 block -translate-x-1/2 cursor-pointer"
              style={{ bottom: "2%", width: "82%", height: "16%" }}
            />
          </div>
        </section>
      </section>

      <div className="fixed inset-x-0 bottom-0 z-50 border-t border-gray-200 bg-white/90 backdrop-blur">
        <div className="mx-auto flex max-w-4xl items-center justify-center px-6 py-4">
          <a href="https://buly.kr/DPVbPT9" target="_blank" rel="noreferrer" className="w-full">
            <button
              type="button"
              className="inline-flex h-[56px] w-full items-center justify-center rounded-2xl bg-[#6F63F6] font-semibold text-white shadow-[0_8px_24px_rgba(111,99,246,0.35)] hover:opacity-95 active:opacity-90"
            >
              신청하기
            </button>
          </a>
        </div>
      </div>
    </div>
  );
}
